import { WARRANTY_TERMS } from "@/lib/businessSchema";
// City + Service combination data for geo-targeted landing pages.
// Each combo renders a unique long-form page at /<service>-<city>-az
// with city-specific copy, FAQ, schema, and internal linking.

export interface CityServiceTemplate {
  serviceSlug: string;
  serviceName: string;
  parentPath: string; // canonical service page this rolls up to
  shortPitch: string; // one-line value prop
  introTemplate: (city: string) => string;
  challengesTemplate: (city: string) => string;
  processBullets: string[];
  startingPrice: string;
  pricingNote: string;
  faqs: (city: string) => { question: string; answer: string }[];
  primaryKeyword: (city: string) => string;
}

export interface CityServiceCombo {
  slug: string; // e.g. "tile-grout-cleaning-mesa-az"
  serviceSlug: string;
  citySlug: string;
}

export const cityServiceTemplates: Record<string, CityServiceTemplate> = {
  "tile-grout-cleaning": {
    serviceSlug: "tile-grout-cleaning",
    serviceName: "Tile & Grout Cleaning",
    parentPath: "/services/tile-grout-cleaning",
    shortPitch: "Truck-mounted hot-water extraction at 1,200 PSI restores tile and grout that mopping can never reach.",
    introTemplate: (city) =>
      `If your ${city} home's tile floors look hazy, your grout has darkened to a color you don't recognize, or your shower never feels truly clean, the problem isn't your mop. It's bacteria, hard-water minerals, and embedded soil packed deep inside porous grout joints that household cleaners physically cannot dissolve. Lazona Tile Care has been deep-cleaning tile and grout for ${city} homeowners since 2016, using truck-mounted equipment that flushes the contamination out at 1,200 PSI and leaves your floors dry, sealed, and 80% lighter the same day.`,
    challengesTemplate: (city) =>
      `${city}'s tap water carries 12-18 grains of mineral hardness per gallon, far above what residential tile is designed to handle. Combine that with monsoon dust, pool chlorine tracked in from the patio, and the foot traffic of a typical Arizona household, and grout that started out white turns gray within 18 months. We see it every week. The good news: 95% of the time, the grout itself is fine. It just needs the right chemistry and the right pressure to come back.`,
    processBullets: [
      "Pre-inspection and grout color match to your original installation",
      "Alkaline pre-spray dwell to break down embedded soils and biofilm",
      "Truck-mounted 220°F hot-water extraction at 1,200 PSI",
      "Acid-neutral rinse and high-velocity drying (floors walkable in 30 min)",
      "Optional same-day color sealing for a permanent finish",
    ],
    startingPrice: "Starting at $1.00/sqft",
    pricingNote: "$750 service minimum. Scope is confirmed during a $150 consultation.",
    faqs: (city) => [
      {
        question: `How much does professional tile and grout cleaning cost in ${city}?`,
        answer: `Cleaning in ${city} starts at $1.00 per square foot with a $750 service minimum. Consultations cost $150. Actual test spots cost $250, credited if booked. We no longer offer free estimates.`,
      },
      {
        question: `Will the cleaning damage my grout or sealer?`,
        answer: `No. We use IICRC-certified extraction methods at controlled pressure and pH. Cheap rotary scrubbers and box-store cleaners erode grout ,  our process is what manufacturers actually recommend.`,
      },
      {
        question: `How long does a typical ${city} job take?`,
        answer: `A 1,200-1,800 sqft home is usually a 3-5 hour job. Floors are walkable in 30 minutes and fully dry within 2 hours. We work around your schedule.`,
      },
      {
        question: `Do you serve all of ${city}?`,
        answer: `Yes. We serve ${city} and surrounding East Valley cities. No trip charge in Chandler, Mesa, Gilbert or Tempe. The charge is $75 outside those cities, or $150 for Scottsdale, Paradise Valley, Glendale and Peoria.`,
      },
    ],
    primaryKeyword: (city) => `tile and grout cleaning ${city} AZ`,
  },

  "grout-color-sealing": {
    serviceSlug: "grout-color-sealing",
    serviceName: "Grout Color Sealing",
    parentPath: "/grout-color-sealing",
    shortPitch: "Permanent pigmented sealer that locks in any grout color, blocks stains, and ends mop-and-pray maintenance.",
    introTemplate: (city) =>
      WARRANTY_TERMS,
    challengesTemplate: (city) =>
      `Most ${city} homes were built with sanded cementitious grout that was never sealed properly at install ,  or the original sealer wore off years ago. Once that happens, every spill, every footprint, every drop of hard water gets absorbed and locked in. No amount of mopping pulls it back out. Color sealing solves the root cause instead of fighting it monthly.`,
    processBullets: [
      "Deep clean and acid-neutralize all grout joints",
      "Color match against 30+ standard manufacturer colors (or custom blend)",
      "Two-coat application of pigmented urethane sealer",
      "Cure according to the installed product's care instructions",
      "Grout color sealing comes with a 15-year warranty for homeowners. It covers the color sealer, not the tile, the installation underneath or other surfaces. Commercial work is not covered by the 15-year warranty.",
    ],
    startingPrice: "Starting at $3.00/sqft",
    pricingNote: "$750 service minimum. Repairs quoted separately from a $1,500 minimum day rate, no exclusions.",
    faqs: (city) => [
      {
        question: `Does color sealing actually last?`,
        answer: WARRANTY_TERMS,
      },
      {
        question: `Can I change my grout color?`,
        answer: `Absolutely. This is one of the most popular reasons ${city} homeowners book the service. We carry every major grout color and can also custom blend if you want something specific.`,
      },
      {
        question: `Is it slippery? Will it look painted?`,
        answer: `No to both. The sealer absorbs into the grout joint, not on top. The finish is matte and identical in texture to fresh grout ,  most clients can't tell where the original ends and the sealer begins.`,
      },
      {
        question: `How long until I can use the floor?`,
        answer: `Walkable in 4 hours, mop-safe in 24 hours. We schedule around ${city} families with pets, kids, and rental tenants every week.`,
      },
    ],
    primaryKeyword: (city) => `grout color sealing ${city} AZ`,
  },

  "travertine-restoration": {
    serviceSlug: "travertine-restoration",
    serviceName: "Travertine Restoration",
    parentPath: "/travertine-restoration",
    shortPitch: "Diamond honing, hole-filling, and resealing that erases acid etching, scratches, and the dull haze on travertine.",
    introTemplate: (city) =>
      `Travertine is everywhere in ${city} ,  entryways, master baths, pool decks, fireplace surrounds ,  because nothing else gives you that warm, organic stone look. The problem: travertine is calcium-based, which means orange juice, vinegar, wine, pet urine, and even Windex eat it alive. By year three or four, ${city} travertine almost always looks dull, etched, and pitted. We bring it back to factory-new with diamond abrasives, premium fillers, and the right sealer for our climate.`,
    challengesTemplate: (city) =>
      `Many ${city} travertine floors can be restored rather than replaced. Etching can be honed out, while open holes and uneven edges require a condition-specific repair plan. Travertine restoration starts at $5.00 per square foot; preparation and finish determine the written quote.`,
    processBullets: [
      "Diamond honing through 50, 100, 200, 400, and 800 grit",
      "Color-matched epoxy fill for open holes and pitting",
      "Final polish to honed, satin, or high-gloss finish (your choice)",
      "Penetrating impregnator sealer rated for 5-7 years",
      "Grout color sealing comes with a 15-year warranty for homeowners. It covers the color sealer, not the tile, the installation underneath or other surfaces. Commercial work is not covered by the 15-year warranty.",
    ],
    startingPrice: "Starting at $5.00/sqft",
    pricingNote: `Travertine restoration starts at $5.00 per square foot. All repairs start at a $1,500 minimum day rate; the written scope confirms preparation and finish.`,
    faqs: (city) => [
      {
        question: `Can you save my etched travertine, or do I need to replace it?`,
        answer: `In 9 out of 10 ${city} homes, restoration is the right call. Even if every single tile is etched, hole-pitted, or scratched, our diamond grit sequence resurfaces the actual stone ,  you keep the original installation, you save 70-80% versus replacement, and the result is honestly better than new.`,
      },
      {
        question: `What finish should I choose ,  honed, satin, or polished?`,
        answer: `Most ${city} clients choose honed (matte) for a modern look or satin for traditional. Polished travertine is dramatic but shows every footprint. We'll bring physical samples to your $150 consultation so you can see them in your own light.`,
      },
      {
        question: `How long does restoration take?`,
        answer: `A 500 sqft area is one full day. Larger areas or pool decks run 2-3 days. The floor is walkable the same evening and fully sealed within 24 hours.`,
      },
      {
        question: `Will it stain again?`,
        answer: "An impregnating sealer helps resist staining, but spills should still be cleaned promptly. It does not prevent etching. Grout color sealing comes with a 15-year warranty for homeowners. It covers the color sealer, not the tile, the installation underneath or other surfaces. Commercial work is not covered by the 15-year warranty.",
      },
    ],
    primaryKeyword: (city) => `travertine restoration ${city} AZ`,
  },

  "marble-polishing": {
    serviceSlug: "marble-polishing",
    serviceName: "Marble Polishing",
    parentPath: "/marble-polishing",
    shortPitch: "Diamond-pad polishing that removes etching, scratches, and water rings ,  restoring true mirror gloss to marble.",
    introTemplate: (city) =>
      `Marble in ${city} kitchens, master baths, and entryways takes a beating. A single splash of lemon juice etches Carrara in under a minute. Hard water ring-marks every shower threshold. Years of foot traffic carve dull paths into the polish. Lazona Tile Care has been polishing marble for ${city} homeowners since 2016 ,  diamond pad sequencing through 50 to 3,000 grit produces a true factory mirror finish, not a topical wax that wears off in six months.`,
    challengesTemplate: (city) =>
      `Most "marble polishing" services in ${city} apply a topical crystallizer or wax. It looks great for 90 days, then it peels and traps dirt underneath. Real marble polishing is mechanical: we abrade the stone with progressively finer diamond pads until the surface itself is glass-smooth. The shine is the marble, not a coating. That's why our work lasts 5-10 years instead of months.`,
    processBullets: [
      "Inspection and severity grading (light etch through deep gouge)",
      "Diamond pad polish through 50 → 200 → 400 → 800 → 1,500 → 3,000 grit",
      "Crystallization step for full mirror reflectivity",
      "Penetrating sealer to slow future etching",
      "Grout color sealing comes with a 15-year warranty for homeowners. It covers the color sealer, not the tile, the installation underneath or other surfaces. Commercial work is not covered by the 15-year warranty.",
    ],
    startingPrice: "Starting at $6/sqft",
    pricingNote: "Most marble polishing projects in this area run $800-$2,500.",
    faqs: (city) => [
      {
        question: `My marble is etched and dull ,  can it be saved?`,
        answer: `Almost always yes. Etching is surface damage that diamond polishing removes completely. We restore Carrara, Calacatta, Statuario, and Crema Marfil in ${city} homes every week. Replacement is rarely necessary.`,
      },
      {
        question: `Will the polish prevent future etching?`,
        answer: `Polish improves stain resistance because the surface is sealed tighter, but no marble is acid-proof. We seal after polishing and walk you through a simple maintenance routine ,  most ${city} clients only see us once every 5-7 years.`,
      },
      {
        question: `Do you polish countertops or only floors?`,
        answer: `Both. Marble countertops, vanity tops, kitchen islands, fireplace surrounds, and shower walls ,  anywhere you have marble in your ${city} home, we polish it on site with no mess.`,
      },
      {
        question: `How is this different from a sealer?`,
        answer: `Sealer is liquid that soaks in and resists stains. Polishing is mechanical resurfacing that brings back the mirror finish. Most ${city} homes need both ,  we always seal after we polish.`,
      },
    ],
    primaryKeyword: (city) => `marble polishing ${city} AZ`,
  },

  "shower-restoration": {
    serviceSlug: "shower-restoration",
    serviceName: "Shower Restoration",
    parentPath: "/services/shower-restoration",
    shortPitch: "Hard-water removal, mold treatment, regrouting, recaulking, and waterproofing ,  your shower like new in one day.",
    introTemplate: (city) =>
      `${city}'s hard water is brutal on showers. Within two years, the glass clouds with mineral scale, the grout darkens with mildew, the silicone caulk peels, and water starts working its way behind the tile. Left alone, this turns into a $5,000-$15,000 water damage claim. Lazona Tile Care restores ${city} showers in a single day ,  deep clean, mold treatment, grout repair and color seal, fresh medical-grade silicone caulk, and a sealed glass door. You can shower again in 24 hours.`,
    challengesTemplate: (city) =>
      `Hard water plus ${city} humidity in a closed shower stall creates the ideal environment for biofilm and mineral scale. Most homeowners try acidic cleaners and harsh scrubbing ,  both of which strip the grout and accelerate the damage. The right approach is targeted chemistry: alkaline degreaser for biofilm, controlled acid for scale, then full re-waterproofing of every joint. Done correctly, this is a 15-year fix, not a 6-month bandage.`,
    processBullets: [
      "Damage assessment for water intrusion behind tile",
      "Specialized hard-water and mold removal treatments",
      "Failing grout removed and replaced; color sealed for permanent uniformity",
      "Old caulk fully removed; medical-grade mildew-resistant silicone replaced",
      "Glass door scale removal and protective coating",
    ],
    startingPrice: "Starting at $1,500",
    pricingNote: "Standard shower $1,500. Larger or natural-stone showers $2,000-$3,000. One-day completion.",
    faqs: (city) => [
      {
        question: `How do I know if my ${city} shower needs restoration?`,
        answer: `Warning signs: cracked or missing grout, mildew that returns within days of cleaning, peeling caulk, loose tile, or water staining on adjacent walls. $150 in-home consultation ,  we'll tell you the truth, including if you don't actually need our service.`,
      },
      {
        question: `Will the hard-water removal damage my glass or tile?`,
        answer: `No. We use pH-controlled treatments specifically designed for shower environments. Big-box hard-water removers and Magic Eraser-style abrasives etch the glass ,  ours doesn't.`,
      },
      {
        question: `Can you stop my shower from getting moldy again?`,
        answer: `Yes. After restoration we color-seal the grout (mold cannot grow on sealed grout) and replace caulk with mildew-resistant silicone. Maintenance is a weekly squeegee ,  no scrubbing required.`,
      },
      {
        question: `How long can I not use the shower?`,
        answer: `24 hours. We do most ${city} shower restorations in one day; you sleep, the silicone cures overnight, and you're back to normal in the morning.`,
      },
    ],
    primaryKeyword: (city) => `shower restoration ${city} AZ`,
  },
};

// Cities we have full LocationData for AND that Google has indexed combos for
export const cityServiceCityList = [
  "chandler",
  "mesa",
  "gilbert",
  "ahwatukee",
  "paradise-valley",
] as const;

export type CityServiceCitySlug = (typeof cityServiceCityList)[number];

// Generate the full matrix of combos
export const cityServiceCombos: CityServiceCombo[] = (() => {
  const combos: CityServiceCombo[] = [];
  for (const serviceSlug of Object.keys(cityServiceTemplates)) {
    for (const citySlug of cityServiceCityList) {
      combos.push({
        slug: `${serviceSlug}-${citySlug}-az`,
        serviceSlug,
        citySlug,
      });
    }
  }
  return combos;
})();

export function findCityServiceCombo(slug: string): CityServiceCombo | undefined {
  return cityServiceCombos.find((c) => c.slug === slug);
}
